import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { notification, Grid, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import moment from "moment";

import { Input, Button, Box, Loading } from "components";
import {
  SendCompleteIcon,
  DownloadIcon,
  LeftPaginateArrow,
  RightPaginateArrow,
  ToggleCollapseIcon,
  PopUpIcon,
} from "icons";
import { generateCSV, localStorageHelper } from "utils";
import { useSmartLoading } from "hooks";
import { GetDashboardInfoActionCreators } from "redux/rootActions";

import {
  selectIsLoadingDashboardInfo,
  selectDashboardInfoErrorMessage,
  dashboardInfoResponse,
} from "redux/dashboard/dashboardInfo/dashboardInfoStates";
import { settingInformationDataResponse } from "redux/setting/general/generalSettingStates";

import {
  ItemTab,
  Title,
  WrapperTab,
  WrapperTabs,
  WrapperDashboardContent,
  WrapperPagination,
  YearMonthPagination,
  PaginationItem,
  ContentTableLeft,
  ContentTableRight,
  TableContent,
  TitlePopupWrapper,
  TableWrapper,
} from "./Dashboard.style";
import { LoadingWrapper } from "screens/userList/UserList.style";

const { useBreakpoint } = Grid;
// md 768

const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const avalableMonths = [7, 8, 9, 10, 11, 12];

const Tabs: {
  key: number;
  name: string;
}[] = [
  {
    name: "登録者数",
    key: 1,
  },
  {
    name: "相談数",
    key: 2,
  },
];

const years: { id: number; value: number }[] = [];
for (let i = moment().year(); i >= 2025; i--) {
  years.push({
    id: i,
    value: i,
  });
}

const Dashboard2 = () => {
  const screens = useBreakpoint();
  const dispatch = useDispatch();

  const contentRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const isLoadingDashboard = useSelector(selectIsLoadingDashboardInfo);
  const dashboardError = useSelector(selectDashboardInfoErrorMessage);
  const dashboardData = useSelector(dashboardInfoResponse);
  const settingInformation = useSelector(settingInformationDataResponse);

  const [dashboardInfo, setDashboardInfo] = useState<any>(undefined);
  const [tab, setTab] = useState(1);
  const [collapsedYears, setCollapsedYears] = useState<Set<number>>(
    new Set([moment().year()])
  );

  const loadingDashboard = useSmartLoading(isLoadingDashboard);

  const isHouseHoldBased = useMemo<boolean>(() => {
    return settingInformation?.result?.super_city_condition?.is_household_based;
  }, [settingInformation]);
  const isOnlineConsultation = useMemo<boolean>(() => {
    return dashboardData?.result?.has_online_medical;
  }, [dashboardData]);
  const isHouseholdBasedConsultation = useMemo<boolean>(() => {
    return (
      dashboardData?.result?.has_consultation_service_type &&
      dashboardData?.result?.has_consultation_household_based
    );
  }, [dashboardData]);
  const isHouseholdBasedOnlineMedical = useMemo<boolean>(() => {
    return (
      dashboardData?.result?.has_online_medical_service_type &&
      dashboardData?.result?.has_online_medical_household_based
    );
  }, [dashboardData]);

  const [currentYear, setCurrentYear] = useState<number>(moment().year());
  const [currentMonth, setCurrentMonth] = useState<number>(
    moment().month() + 1
  );

  const superCityName = localStorageHelper.getItem("supercity_name");

  const returnDashboardData = useCallback(
    (day: number) => {
      if (!dashboardData?.result?.daily_stats?.length) return undefined;
      const data = dashboardData?.result?.daily_stats?.find(
        (item: any) => item.date === day
      );
      return data;
    },
    [dashboardData]
  );

  const returnTotalGender = useCallback(
    (day: number) => {
      const data = returnDashboardData(day);
      return (
        data?.breakdown?.patient?.gender_0 +
        data?.breakdown?.patient?.gender_1 +
        data?.breakdown?.patient?.gender_2
      );
    },
    [dashboardData]
  );

  // Utility function to calculate sum of gender values across all days
  const calculateGenderSum = useCallback(
    (
      dataArray: any[],
      path: string,
      genderType: "gender_0" | "gender_1" | "gender_2" | "total"
    ): number => {
      if (!dataArray || !Array.isArray(dataArray)) return 0;

      return dataArray.reduce((sum, item) => {
        // Navigate to the nested path (e.g., 'breakdown.patient', 'consultation.eligible', etc.)
        const pathParts = path.split(".");
        let current = item;

        for (const part of pathParts) {
          if (current && typeof current === "object" && part in current) {
            current = current[part];
          } else {
            return sum; // Path doesn't exist, skip this item
          }
        }

        // Add the gender value if it exists
        if (current && typeof current === "object" && genderType in current) {
          return sum + (current[genderType] || 0);
        }

        return sum;
      }, 0);
    },
    []
  );

  // Example usage functions for common calculations
  const calculatePatientGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "breakdown.patient",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculatePatientTotalSum = useCallback(
    (
      genderType: "gender_0" | "gender_1" | "gender_2" | "total" | "total_users"
    ): number => {
      if (!dashboardData?.result?.daily_stats?.length) return 0;

      return dashboardData.result.daily_stats.reduce((sum: any, item: any) => {
        if (item && typeof item === "object" && genderType in item) {
          return sum + (item[genderType] || 0);
        }

        return sum;
      }, 0);
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateConsultationEligibleGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "consultation.eligible",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateConsultationHouseholdGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "consultation.household",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateConsultationsGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "consultations",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateConsultationsTargetGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "consultations.target",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateConsultationsHouseholdGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "consultations.household",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateOnlineMedicalEligibleGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "online_medical.eligible",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateOnlineMedicalHouseholdGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "online_medical.household",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const daysOfMonth = useMemo(() => {
    const daysInMonth = moment(
      `${currentYear}-${currentMonth}`,
      "YYYY-MM"
    ).daysInMonth();
    return Array.from({ length: daysInMonth }, (_, i) => ({
      id: i + 1,
      value: i + 1,
    }));
  }, [currentMonth, currentYear]);

  useEffect(() => {
    dispatch(
      GetDashboardInfoActionCreators.getDashboardInfoAction({
        year: currentYear,
        month: currentMonth,
      })
    );
  }, [currentMonth, currentYear]);

  useEffect(() => {
    if (dashboardData && !isLoadingDashboard) {
      setDashboardInfo(dashboardData);
    }
  }, [dashboardData, isLoadingDashboard]);

  useEffect(() => {
    if (dashboardError) {
      // notification.error({
      //   message: "ダッシュボード情報の取得に失敗しました",
      // });
      dispatch(GetDashboardInfoActionCreators.handleResetAction());
      setDashboardInfo(undefined);
    }
  }, [dashboardError]);

  // useEffect(() => {
  //   dispatch(
  //     GetDashboardInfoActionCreators.getDashboardInfoAction({
  //       year: currentYear,
  //       month: currentMonth,
  //     })
  //   );
  // }, []);
  const handleToggleYear = useCallback((yearValue: number) => {
    setCollapsedYears((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(yearValue)) {
        newSet.delete(yearValue);
      } else {
        // Close all other years first
        newSet.clear();
        newSet.add(yearValue);
      }
      return newSet;
    });
  }, []);

  const isYearCollapsed = useCallback(
    (yearValue: number) => {
      return collapsedYears.has(yearValue);
    },
    [collapsedYears]
  );

  return (
    <React.Fragment>
      <Title>ダッシュボード</Title>

      {/* <WrapperTabs>
        <WrapperTab>
          {Tabs.map((tabItem) => (
            <ItemTab key={tabItem.key}>
              <div
                className={tab === tabItem.key ? "tab-active" : ""}
                onClick={() => {
                  setTab(tabItem.key);
                }}
              >
                {tabItem.name}
              </div>
            </ItemTab>
          ))}
        </WrapperTab>
      </WrapperTabs> */}

      <WrapperDashboardContent>
        <WrapperPagination>
          <LeftPaginateArrow
            onClick={() => {
              if (
                currentYear &&
                currentYear >= 2025 &&
                currentYear < moment().year()
              ) {
                handleToggleYear(currentYear + 1);
                // setCollapsedYears(new Set([currentYear + 1]));
                setCurrentYear(currentYear + 1);
                if (currentYear + 1 === moment().year()) {
                  setCurrentMonth(moment().month() + 1);
                } else {
                  setCurrentMonth(1);
                }
              }
            }}
          />
          <YearMonthPagination>
            {years.map((year: any) => (
              <PaginationItem
                key={year.id}
                collapsed={isYearCollapsed(year.value)}
              >
                <div
                  className="label"
                  onClick={() => handleToggleYear(year.value)}
                  style={{ cursor: "pointer" }}
                >
                  {year.value}
                  <ToggleCollapseIcon
                    style={{
                      transform: isYearCollapsed(year.value)
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </div>
                {isYearCollapsed(year.value) && (
                  <div className="months">
                    {year.value === 2025
                      ? avalableMonths.map((monthItem: any, index: number) => (
                          <div
                            key={index}
                            className={`month-item${
                              year.value === currentYear &&
                              monthItem === currentMonth
                                ? " active"
                                : ""
                            }${
                              year.value === moment().year() &&
                              monthItem > moment().month() + 1
                                ? " disabled"
                                : ""
                            }${
                              isYearCollapsed(year.value) ? " collapsed" : ""
                            }`}
                            onClick={() => {
                              if (
                                year.value !== moment().year() ||
                                (year.value === moment().year() &&
                                  monthItem <= moment().month() + 1)
                              ) {
                                setCurrentMonth(monthItem);
                                setCurrentYear(year.value);
                              }
                            }}
                          >
                            {monthItem}
                          </div>
                        ))
                      : months.map((monthItem: any, index: number) => (
                          <div
                            key={index}
                            className={`month-item${
                              year.value === currentYear &&
                              monthItem === currentMonth
                                ? " active"
                                : ""
                            }${
                              year.value === moment().year() &&
                              monthItem > moment().month() + 1
                                ? " disabled"
                                : ""
                            }${
                              isYearCollapsed(year.value) ? " collapsed" : ""
                            }`}
                            onClick={() => {
                              if (
                                year.value !== moment().year() ||
                                (year.value === moment().year() &&
                                  monthItem <= moment().month() + 1)
                              ) {
                                setCurrentMonth(monthItem);
                                setCurrentYear(year.value);
                              }
                            }}
                          >
                            {monthItem}
                          </div>
                        ))}
                  </div>
                )}
              </PaginationItem>
            ))}
          </YearMonthPagination>
          <RightPaginateArrow
            onClick={() => {
              if (
                currentYear &&
                currentYear > 2025 &&
                currentYear <= moment().year()
              ) {
                handleToggleYear(currentYear - 1);
                // setCollapsedYears(new Set([currentYear + 1]));
                setCurrentYear(currentYear - 1);
                setCurrentMonth(1);
              }
            }}
          />
        </WrapperPagination>

        <div
          style={{
            width: "100%",
            borderTop: "1px dashed #cccccc",
            margin: "16px 0px 24px",
          }}
        ></div>

        <TableWrapper
          isHouseholdBasedConsultation={isHouseholdBasedConsultation}
        >
          <TableContent>
            <TitlePopupWrapper>
              <div className="title-popup">登録総数</div>
              <div
                className={`popup-btn${
                  !dashboardData?.result?.google_sheet_urls?.patient
                    ? " disabled-btn"
                    : ""
                }`}
                onClick={() => {
                  if (dashboardData?.result?.google_sheet_urls?.patient) {
                    window.open(
                      dashboardData?.result?.google_sheet_urls?.patient,
                      "_blank"
                    );
                  }
                }}
              >
                {loadingDashboard ? (
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{ fontSize: 20, color: "white" }}
                        spin
                      />
                    }
                    size="small"
                    style={{ margin: "2px auto 0px 51px" }}
                  />
                ) : (
                  <>
                    <PopUpIcon />
                    <div className="name">スプレッドシート</div>
                  </>
                )}
              </div>
            </TitlePopupWrapper>
            <ContentTableLeft loading={loadingDashboard}>
              {loadingDashboard && (
                <LoadingWrapper style={{ width: 25, height: 25, top: 100 }}>
                  <Loading width="25" height="25" />
                </LoadingWrapper>
              )}

              <div className="first-block">
                <div>{currentMonth}月</div>
                <div></div>
                <div></div>
                {daysOfMonth.map((day) => (
                  <div key={day.id}>{day.value}</div>
                ))}
                <div>合計</div>
              </div>
              <div className="second-block">
                <div>内訳</div>
                <div style={{ display: "flex", width: "100%" }}>
                  <div className="block-1">
                    <div>人</div>
                    <div style={{ display: "flex" }}>
                      <div className="col-1">
                        <div>男</div>
                        {daysOfMonth.map((day) => (
                          <div key={day.id}>
                            {returnDashboardData(day.value)?.breakdown?.patient
                              ?.gender_0 ||
                            returnDashboardData(day.value)?.breakdown?.patient
                              ?.gender_0 === 0
                              ? returnDashboardData(day.value)?.breakdown
                                  ?.patient?.gender_0
                              : "-"}
                          </div>
                        ))}
                        <div>{calculatePatientGenderSum("gender_0")}</div>
                      </div>
                      <div className="col-2">
                        <div>女</div>
                        {daysOfMonth.map((day) => (
                          <div key={day.id}>
                            {returnDashboardData(day.value)?.breakdown?.patient
                              ?.gender_1 ||
                            returnDashboardData(day.value)?.breakdown?.patient
                              ?.gender_1 === 0
                              ? returnDashboardData(day.value)?.breakdown
                                  ?.patient?.gender_1
                              : "-"}
                          </div>
                        ))}
                        <div>{calculatePatientGenderSum("gender_1")}</div>
                      </div>
                      <div className="col-3">
                        <div>なし</div>
                        {daysOfMonth.map((day) => (
                          <div style={{ textAlign: "right" }} key={day.id}>
                            {returnDashboardData(day.value)?.breakdown?.patient
                              ?.gender_2 ||
                            returnDashboardData(day.value)?.breakdown?.patient
                              ?.gender_2 === 0
                              ? returnDashboardData(day.value)?.breakdown
                                  ?.patient?.gender_2
                              : "-"}
                          </div>
                        ))}
                        <div>{calculatePatientGenderSum("gender_2")}</div>
                      </div>
                    </div>
                  </div>
                  {/* <div className="block-2">
                    <div>合計</div>
                    <div></div>
                    <div>1252</div>
                    <div>3222</div>
                    <div>10345</div>
                  </div> */}
                  <div className="block-3">
                    <div>世帯</div>
                    <div></div>
                    {daysOfMonth.map((day) => (
                      <div key={day.id}>
                        {returnDashboardData(day.value)?.total_users ||
                        returnDashboardData(day.value)?.total_users === 0
                          ? returnDashboardData(day.value)?.total_users
                          : "-"}
                      </div>
                    ))}
                    <div>{calculatePatientTotalSum("total_users")}</div>
                  </div>
                </div>
              </div>
              <div className="third-block">
                <div>医療相談</div>
                <div style={{ display: "flex", width: "100%" }}>
                  <div className="block-1">
                    <div>対象者</div>
                    <div style={{ display: "flex" }}>
                      <div className="col-1">
                        <div>男</div>
                        {daysOfMonth.map((day) => (
                          <div key={day.id}>
                            {returnDashboardData(day.value)?.consultation
                              ?.eligible?.gender_0 ||
                            returnDashboardData(day.value)?.consultation
                              ?.eligible?.gender_0 === 0
                              ? returnDashboardData(day.value)?.consultation
                                  ?.eligible?.gender_0
                              : "-"}
                          </div>
                        ))}
                        <div>
                          {calculateConsultationEligibleGenderSum("gender_0")}
                        </div>
                      </div>
                      <div className="col-2">
                        <div>女</div>
                        {daysOfMonth.map((day) => (
                          <div key={day.id}>
                            {returnDashboardData(day.value)?.consultation
                              ?.eligible?.gender_1 ||
                            returnDashboardData(day.value)?.consultation
                              ?.eligible?.gender_1 === 0
                              ? returnDashboardData(day.value)?.consultation
                                  ?.eligible?.gender_1
                              : "-"}
                          </div>
                        ))}
                        <div>
                          {calculateConsultationEligibleGenderSum("gender_1")}
                        </div>
                      </div>
                      <div className="col-3">
                        <div>なし</div>
                        {daysOfMonth.map((day) => (
                          <div style={{ textAlign: "right" }} key={day.id}>
                            {returnDashboardData(day.value)?.consultation
                              ?.eligible?.gender_2 ||
                            returnDashboardData(day.value)?.consultation
                              ?.eligible?.gender_2 === 0
                              ? returnDashboardData(day.value)?.consultation
                                  ?.eligible?.gender_2
                              : "-"}
                          </div>
                        ))}
                        <div>
                          {calculateConsultationEligibleGenderSum("gender_2")}
                        </div>
                      </div>
                    </div>
                  </div>
                  {isHouseholdBasedConsultation && (
                    <div className="block-2">
                      <div>世帯対象者</div>
                      <div style={{ display: "flex" }}>
                        <div className="col-1">
                          <div>男</div>
                          {daysOfMonth.map((day) => (
                            <div key={day.id}>
                              {returnDashboardData(day.value)?.consultation
                                ?.household?.gender_0 ||
                              returnDashboardData(day.value)?.consultation
                                ?.household?.gender_0 === 0
                                ? returnDashboardData(day.value)?.consultation
                                    ?.is_household_based
                                  ? returnDashboardData(day.value)?.consultation
                                      ?.household?.gender_0
                                  : "-"
                                : "-"}
                            </div>
                          ))}
                          <div>
                            {calculateConsultationHouseholdGenderSum(
                              "gender_0"
                            )}
                          </div>
                        </div>
                        <div className="col-2">
                          <div>女</div>
                          {daysOfMonth.map((day) => (
                            <div key={day.id}>
                              {returnDashboardData(day.value)?.consultation
                                ?.household?.gender_1 ||
                              returnDashboardData(day.value)?.consultation
                                ?.household?.gender_1 === 0
                                ? returnDashboardData(day.value)?.consultation
                                    ?.is_household_based
                                  ? returnDashboardData(day.value)?.consultation
                                      ?.household?.gender_1
                                  : "-"
                                : "-"}
                            </div>
                          ))}
                          <div>
                            {calculateConsultationHouseholdGenderSum(
                              "gender_1"
                            )}
                          </div>
                        </div>
                        <div className="col-3">
                          <div>なし</div>
                          {daysOfMonth.map((day) => (
                            <div style={{ textAlign: "right" }} key={day.id}>
                              {returnDashboardData(day.value)?.consultation
                                ?.household?.gender_2 ||
                              returnDashboardData(day.value)?.consultation
                                ?.household?.gender_2 === 0
                                ? returnDashboardData(day.value)?.consultation
                                    ?.is_household_based
                                  ? returnDashboardData(day.value)?.consultation
                                      ?.household?.gender_2
                                  : "-"
                                : "-"}
                            </div>
                          ))}
                          <div style={{ textAlign: "right" }}>
                            {calculateConsultationHouseholdGenderSum(
                              "gender_2"
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {isOnlineConsultation ? (
                <div className="third-block" style={{ borderRight: "none" }}>
                  <div>オンライン診療</div>
                  <div style={{ display: "flex", width: "100%" }}>
                    <div
                      className="block-1"
                      style={{
                        minWidth: isHouseholdBasedOnlineMedical
                          ? "112px"
                          : "122px",
                      }}
                    >
                      <div>対象者</div>
                      <div style={{ display: "flex" }}>
                        <div
                          className="col-1"
                          style={{
                            minWidth: isHouseholdBasedOnlineMedical
                              ? "36px"
                              : "40px",
                          }}
                        >
                          <div>男</div>
                          {daysOfMonth.map((day) => (
                            <div key={day.id}>
                              {
                                returnDashboardData(day.value)?.online_medical
                                  ?.eligible?.gender_0 ||
                                returnDashboardData(day.value)?.online_medical
                                  ?.eligible?.gender_0 === 0
                                  ? returnDashboardData(day.value)
                                      ?.online_medical?.eligible?.gender_0
                                  : "-"
                                // returnDashboardData(day.value)?.online_medical
                                //   ?.is_available
                                //   ? returnDashboardData(day.value)
                                //       ?.online_medical?.eligible?.gender_0 || 0
                                //   : "-"
                                // returnDashboardData(day.value)
                                //   ?.online_medical &&
                                //   "is_available" in
                                //   returnDashboardData(day.value)
                                //     ?.online_medical &&
                                //   !returnDashboardData(day.value)
                                //     ?.online_medical?.is_available
                                //   ? "-"
                                //   : 0
                              }
                            </div>
                          ))}
                          <div>
                            {calculateOnlineMedicalEligibleGenderSum(
                              "gender_0"
                            )}
                          </div>
                        </div>
                        <div
                          className="col-2"
                          style={{
                            minWidth: isHouseholdBasedOnlineMedical
                              ? "36px"
                              : "40px",
                          }}
                        >
                          <div>女</div>
                          {daysOfMonth.map((day) => (
                            <div key={day.id}>
                              {
                                returnDashboardData(day.value)?.online_medical
                                  ?.eligible?.gender_1 ||
                                returnDashboardData(day.value)?.online_medical
                                  ?.eligible?.gender_1 === 0
                                  ? returnDashboardData(day.value)
                                      ?.online_medical?.eligible?.gender_1
                                  : "-"
                                // returnDashboardData(day.value)
                                //   ?.online_medical &&
                                //   "is_available" in
                                //   returnDashboardData(day.value)
                                //     ?.online_medical &&
                                //   !returnDashboardData(day.value)
                                //     ?.online_medical?.is_available
                                //   ? "-"
                                //   : 0
                              }
                            </div>
                          ))}
                          <div>
                            {calculateOnlineMedicalEligibleGenderSum(
                              "gender_1"
                            )}
                          </div>
                        </div>
                        <div
                          className="col-3"
                          style={{
                            minWidth: isHouseholdBasedOnlineMedical
                              ? "40px"
                              : "43px",
                          }}
                        >
                          <div>なし</div>
                          {daysOfMonth.map((day) => (
                            <div style={{ textAlign: "right" }} key={day.id}>
                              {
                                returnDashboardData(day.value)?.online_medical
                                  ?.eligible?.gender_2 ||
                                returnDashboardData(day.value)?.online_medical
                                  ?.eligible?.gender_2 === 0
                                  ? returnDashboardData(day.value)
                                      ?.online_medical?.eligible?.gender_2
                                  : "-"
                                // returnDashboardData(day.value)
                                //   ?.online_medical &&
                                //   "is_available" in
                                //   returnDashboardData(day.value)
                                //     ?.online_medical &&
                                //   !returnDashboardData(day.value)
                                //     ?.online_medical?.is_available
                                //   ? "-"
                                //   : 0
                              }
                            </div>
                          ))}
                          <div style={{ textAlign: "right" }}>
                            {calculateOnlineMedicalEligibleGenderSum(
                              "gender_2"
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {isHouseholdBasedOnlineMedical && (
                      <div className="block-2">
                        <div>世帯対象者</div>
                        <div style={{ display: "flex" }}>
                          <div className="col-1">
                            <div>男</div>
                            {daysOfMonth.map((day) => (
                              <div key={day.id}>
                                {
                                  returnDashboardData(day.value)?.online_medical
                                    ?.is_available &&
                                  returnDashboardData(day.value)?.online_medical
                                    ?.is_household_based
                                    ? returnDashboardData(day.value)
                                        ?.online_medical?.household?.gender_0 ||
                                      0
                                    : "-"
                                  // returnDashboardData(day.value)
                                  //   ?.online_medical &&
                                  //   "is_available" in
                                  //   returnDashboardData(day.value)
                                  //     ?.online_medical &&
                                  //   !returnDashboardData(day.value)
                                  //     ?.online_medical?.is_available
                                  //   ? "-"
                                  //   : 0
                                }
                              </div>
                            ))}
                            <div>
                              {calculateOnlineMedicalHouseholdGenderSum(
                                "gender_0"
                              )}
                            </div>
                          </div>
                          <div className="col-2">
                            <div>女</div>
                            {daysOfMonth.map((day) => (
                              <div key={day.id}>
                                {
                                  returnDashboardData(day.value)?.online_medical
                                    ?.is_available &&
                                  returnDashboardData(day.value)?.online_medical
                                    ?.is_household_based
                                    ? returnDashboardData(day.value)
                                        ?.online_medical?.household?.gender_1 ||
                                      0
                                    : "-"
                                  // returnDashboardData(day.value)
                                  //   ?.online_medical &&
                                  //   "is_available" in
                                  //   returnDashboardData(day.value)
                                  //     ?.online_medical &&
                                  //   !returnDashboardData(day.value)
                                  //     ?.online_medical?.is_available
                                  //   ? "-"
                                  //   : 0
                                }
                              </div>
                            ))}
                            <div style={{ textAlign: "right" }}>
                              {calculateOnlineMedicalHouseholdGenderSum(
                                "gender_1"
                              )}
                            </div>
                          </div>
                          <div className="col-3">
                            <div>なし</div>
                            {daysOfMonth.map((day) => (
                              <div style={{ textAlign: "right" }} key={day.id}>
                                {
                                  returnDashboardData(day.value)?.online_medical
                                    ?.is_available &&
                                  returnDashboardData(day.value)?.online_medical
                                    ?.is_household_based
                                    ? returnDashboardData(day.value)
                                        ?.online_medical?.household?.gender_2 ||
                                      0
                                    : "-"
                                  // returnDashboardData(day.value)
                                  //   ?.online_medical &&
                                  //   "is_available" in
                                  //   returnDashboardData(day.value)
                                  //     ?.online_medical &&
                                  //   !returnDashboardData(day.value)
                                  //     ?.online_medical?.is_available
                                  //   ? "-"
                                  //   : 0
                                }
                              </div>
                            ))}
                            <div style={{ textAlign: "right" }}>
                              {calculateOnlineMedicalHouseholdGenderSum(
                                "gender_2"
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <></>
              )}
              {/* <div className="fourth-block">
                <div>オンライン診療</div>
                <div>対象者</div>
                <div style={{ display: "flex" }}>
                  <div className="col-1">
                    <div>男</div>
                    <div>510</div>
                    <div>333</div>
                    <div>1234</div>
                  </div>
                  <div className="col-2">
                    <div>女</div>
                    <div>510</div>
                    <div>333</div>
                    <div>1234</div>
                  </div>
                  <div className="col-3">
                    <div>なし</div>
                    <div>10</div>
                    <div>12 style={{ textAlign: "right"}}</div>
                    <div>12</div>
                  </div>
                </div>
              </div> */}
            </ContentTableLeft>
          </TableContent>
          <TableContent>
            <TitlePopupWrapper>
              <div className="title-popup">相談数</div>
              <div
                className={`popup-btn${
                  !dashboardData?.result?.google_sheet_urls?.consultation
                    ? " disabled-btn"
                    : ""
                }`}
                onClick={() => {
                  if (dashboardData?.result?.google_sheet_urls?.consultation) {
                    window.open(
                      dashboardData?.result?.google_sheet_urls?.consultation,
                      "_blank"
                    );
                  }
                }}
              >
                {loadingDashboard ? (
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{ fontSize: 20, color: "white" }}
                        spin
                      />
                    }
                    size="small"
                    style={{ margin: "2px auto 0px 51px" }}
                  />
                ) : (
                  <>
                    <PopUpIcon />
                    <div className="name">スプレッドシート</div>
                  </>
                )}
              </div>
            </TitlePopupWrapper>
            <ContentTableRight loading={loadingDashboard}>
              {loadingDashboard && (
                <LoadingWrapper style={{ width: 25, height: 25, top: 100 }}>
                  <Loading width="25" height="25" />
                </LoadingWrapper>
              )}
              <div className="first-block">
                <div>{currentMonth}月</div>
                <div></div>
                <div></div>
                {daysOfMonth.map((day) => (
                  <div key={day.id}>{day.value}</div>
                ))}
                <div>合計</div>
              </div>
              <div className="second-block">
                <div>内訳</div>
                <div style={{ display: "flex", width: "100%" }}>
                  <div className="block-1">
                    <div></div>
                    <div style={{ display: "flex" }}>
                      <div className="col-1">
                        <div>男</div>
                        {daysOfMonth.map((day) => (
                          <div key={day.id}>
                            {returnDashboardData(day.value)?.consultations
                              ?.gender_0 ||
                            returnDashboardData(day.value)?.consultations
                              ?.gender_0 === 0
                              ? returnDashboardData(day.value)?.consultations
                                  ?.gender_0
                              : "-"}
                          </div>
                        ))}
                        <div>{calculateConsultationsGenderSum("gender_0")}</div>
                      </div>
                      <div className="col-2">
                        <div>女</div>
                        {daysOfMonth.map((day) => (
                          <div key={day.id}>
                            {returnDashboardData(day.value)?.consultations
                              ?.gender_1 ||
                            returnDashboardData(day.value)?.consultations
                              ?.gender_1 === 0
                              ? returnDashboardData(day.value)?.consultations
                                  ?.gender_1
                              : "-"}
                          </div>
                        ))}
                        <div>{calculateConsultationsGenderSum("gender_1")}</div>
                      </div>
                      <div className="col-3">
                        <div>なし</div>
                        {daysOfMonth.map((day) => (
                          <div style={{ textAlign: "right" }} key={day.id}>
                            {returnDashboardData(day.value)?.consultations
                              ?.gender_2 ||
                            returnDashboardData(day.value)?.consultations
                              ?.gender_2 === 0
                              ? returnDashboardData(day.value)?.consultations
                                  ?.gender_2
                              : "-"}
                          </div>
                        ))}
                        <div>{calculateConsultationsGenderSum("gender_2")}</div>
                      </div>
                    </div>
                  </div>
                  {/* <div className="block-2">
                      <div>合計</div>
                      <div></div>
                      <div>1252</div>
                      <div>3222</div>
                      <div>10345</div>
                    </div> */}
                  {/* <div className="block-3">
                                    <div>世帯</div>
                                    <div></div>
                                    <div>200</div>
                                    <div>500</div>
                                    <div>1023</div>
                                </div> */}
                </div>
              </div>
              <div
                className="third-block"
                style={{
                  borderRight: isHouseholdBasedConsultation
                    ? "1px solid #cccccc"
                    : "none",
                }}
              >
                <div></div>
                <div>対象者</div>
                <div style={{ display: "flex" }}>
                  <div className="col-1">
                    <div>男</div>
                    {daysOfMonth.map((day) => (
                      <div key={day.id}>
                        {returnDashboardData(day.value)?.consultations?.target
                          ?.gender_0 ||
                        returnDashboardData(day.value)?.consultations?.target
                          ?.gender_0 === 0
                          ? returnDashboardData(day.value)?.consultations
                              ?.target?.gender_0
                          : "-"}
                      </div>
                    ))}
                    <div>
                      {calculateConsultationsTargetGenderSum("gender_0")}
                    </div>
                  </div>
                  <div className="col-2">
                    <div>女</div>
                    {daysOfMonth.map((day) => (
                      <div key={day.id}>
                        {returnDashboardData(day.value)?.consultations?.target
                          ?.gender_1 ||
                        returnDashboardData(day.value)?.consultations?.target
                          ?.gender_1 === 0
                          ? returnDashboardData(day.value)?.consultations
                              ?.target?.gender_1
                          : "-"}
                      </div>
                    ))}
                    <div>
                      {calculateConsultationsTargetGenderSum("gender_1")}
                    </div>
                  </div>
                  <div className="col-3">
                    <div>なし</div>
                    {daysOfMonth.map((day) => (
                      <div style={{ textAlign: "right" }} key={day.id}>
                        {returnDashboardData(day.value)?.consultations?.target
                          ?.gender_2 ||
                        returnDashboardData(day.value)?.consultations?.target
                          ?.gender_2 === 0
                          ? returnDashboardData(day.value)?.consultations
                              ?.target?.gender_2
                          : "-"}
                      </div>
                    ))}
                    <div style={{ textAlign: "right" }}>
                      {calculateConsultationsTargetGenderSum("gender_2")}
                    </div>
                  </div>
                </div>
              </div>
              {isHouseholdBasedConsultation && (
                <div className="fourth-block">
                  <div></div>
                  <div>世帯対象者</div>
                  <div style={{ display: "flex" }}>
                    <div className="col-1">
                      <div>男</div>
                      {daysOfMonth.map((day) => (
                        <div key={day.id}>
                          {returnDashboardData(day.value)?.consultations
                            ?.household?.gender_0 ||
                          returnDashboardData(day.value)?.consultations
                            ?.household?.gender_0 === 0
                            ? returnDashboardData(day.value)?.consultation
                                ?.is_household_based
                              ? returnDashboardData(day.value)?.consultations
                                  ?.household?.gender_0
                              : "-"
                            : "-"}
                        </div>
                      ))}
                      <div style={{ textAlign: "right" }}>
                        {calculateConsultationsHouseholdGenderSum("gender_0")}
                      </div>
                    </div>
                    <div className="col-2">
                      <div>女</div>
                      {daysOfMonth.map((day) => (
                        <div key={day.id}>
                          {returnDashboardData(day.value)?.consultations
                            ?.household?.gender_1 ||
                          returnDashboardData(day.value)?.consultations
                            ?.household?.gender_1 === 0
                            ? returnDashboardData(day.value)?.consultation
                                ?.is_household_based
                              ? returnDashboardData(day.value)?.consultations
                                  ?.household?.gender_1
                              : "-"
                            : "-"}
                        </div>
                      ))}
                      <div style={{ textAlign: "right" }}>
                        {calculateConsultationsHouseholdGenderSum("gender_1")}
                      </div>
                    </div>
                    <div className="col-3">
                      <div>なし</div>
                      {daysOfMonth.map((day) => (
                        <div style={{ textAlign: "right" }} key={day.id}>
                          {returnDashboardData(day.value)?.consultations
                            ?.household?.gender_2 ||
                          returnDashboardData(day.value)?.consultations
                            ?.household?.gender_2 === 0
                            ? returnDashboardData(day.value)?.consultation
                                ?.is_household_based
                              ? returnDashboardData(day.value)?.consultations
                                  ?.household?.gender_2
                              : "-"
                            : "-"}
                        </div>
                      ))}
                      <div style={{ textAlign: "right" }}>
                        {calculateConsultationsHouseholdGenderSum("gender_2")}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </ContentTableRight>
          </TableContent>
        </TableWrapper>
      </WrapperDashboardContent>

      {/* {tab === 1 ? (
        <></>
      ) : (
        <WrapperDashboardContent>
          <WrapperPagination>
            <LeftPaginateArrow />
            <YearMonthPagination>
              {years.map((year: any) => (
                <PaginationItem key={year.id}>
                  <div 
                    className="label"
                    onClick={() => handleToggleYear(year.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    {year.value}
                    <ToggleCollapseIcon 
                      style={{ 
                        transform: isYearCollapsed(year.value) ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }} 
                    />
                  </div>
                  {!isYearCollapsed(year.value) && (
                    <div className="months">
                      {months.map((month: any, index: number) => (
                        <div
                          key={index}
                          className={`month-item ${index === 3 ? "active" : ""}`}
                        >
                          {month}
                        </div>
                      ))}
                    </div>
                  )}
                </PaginationItem>
              ))}
            </YearMonthPagination>
            <RightPaginateArrow />
          </WrapperPagination>
          <div
            style={{
              width: "100%",
              borderTop: "1px dashed #cccccc",
              margin: "16px 0px",
            }}
          ></div>
          <ContentTableRight>
            <div className="first-block">
              <div>4月</div>
              <div></div>
              <div></div>
              <div>1</div>
              <div>2</div>
              <div>合計</div>
            </div>
            <div className="second-block">
              <div>登録総数</div>
              <div style={{ display: "flex", width: "100%" }}>
                <div className="block-1">
                  <div></div>
                  <div style={{ display: "flex" }}>
                    <div className="col-1">
                      <div>男</div>
                      <div>510</div>
                      <div>333</div>
                      <div>1234</div>
                    </div>
                    <div className="col-2">
                      <div>女</div>
                      <div>510</div>
                      <div>333</div>
                      <div>1234</div>
                    </div>
                    <div className="col-3">
                      <div>なし</div>
                      <div>510</div>
                      <div>333 style={{ textAlign: "right"}}</div>
                      <div>1234</div>
                    </div>
                  </div>
                </div>
                <div className="block-2">
                  <div>合計</div>
                  <div></div>
                  <div>1252</div>
                  <div>3222</div>
                  <div>10345</div>
                </div>
              </div>
            </div>
            <div className="third-block">
              <div></div>
              <div>対象者</div>
              <div style={{ display: "flex" }}>
                <div className="col-1">
                  <div>男</div>
                  <div>510</div>
                  <div>33</div>
                  <div>555</div>
                </div>
                <div className="col-2">
                  <div>女</div>
                  <div>278</div>
                  <div>66</div>
                  <div>333</div>
                </div>
                <div className="col-3">
                  <div>なし</div>
                  <div>10</div>
                  <div>22 style={{ textAlign: "right"}}</div>
                  <div>33</div>
                </div>
              </div>
            </div>
            <div className="fourth-block">
              <div></div>
              <div>世帯対象者</div>
              <div style={{ display: "flex" }}>
                <div className="col-1">
                  <div>男</div>
                  <div>510</div>
                  <div>33</div>
                  <div>33</div>
                </div>
                <div className="col-2">
                  <div>女</div>
                  <div>510</div>
                  <div>66</div>
                  <div>66</div>
                </div>
                <div className="col-3">
                  <div>なし</div>
                  <div>10</div>
                  <div>12 style={{ textAlign: "right"}}</div>
                  <div>12</div>
                </div>
              </div>
            </div>
          </ContentTableRight>
        </WrapperDashboardContent>
      )} */}
    </React.Fragment>
  );
};

export default Dashboard2;
